import isEqual from 'lodash/isEqual'
import throttle from 'lodash/throttle'
import { useEffect, useState } from 'react'
import { useErrorHandling } from '@/tools/o11y/client'

type IntersectionState = {
  isIntersecting: boolean
  isAbove: boolean
  isBelow: boolean
}

const UPDATE_TIME = 50

export function useIntersectionDetection(
  targetElId: string,
  referenceElId: string
): IntersectionState {
  const { execWithErrorTracking } = useErrorHandling()

  const [state, setState] = useState<IntersectionState>({
    isIntersecting: false,
    isAbove: false,
    isBelow: false,
  })

  useEffect(() => {
    execWithErrorTracking(() => {
      const targetEl = document.getElementById(targetElId)
      const referenceEl = document.getElementById(referenceElId)

      if (!targetEl || !referenceEl) {
        return
      }

      const checkPosition = () => {
        const rectTarget = targetEl.getBoundingClientRect()
        const rectReference = referenceEl.getBoundingClientRect()

        const newState: IntersectionState = {
          isIntersecting:
            rectTarget.left < rectReference.right &&
            rectTarget.right > rectReference.left &&
            rectTarget.top < rectReference.bottom &&
            rectTarget.bottom > rectReference.top,
          isAbove: rectTarget.bottom <= rectReference.top,
          isBelow: rectTarget.top >= rectReference.bottom,
        }

        setState((prev) => (isEqual(prev, newState) ? prev : newState))
      }

      const throttledCheck = throttle(checkPosition, UPDATE_TIME)

      throttledCheck()

      window.addEventListener('scroll', throttledCheck, true)
      window.addEventListener('resize', throttledCheck)

      return () => {
        window.removeEventListener('scroll', throttledCheck, true)
        window.removeEventListener('resize', throttledCheck)
        throttledCheck.cancel()
      }
    })
  }, [execWithErrorTracking, targetElId, referenceElId])

  return state
}
